import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ordersAPI } from '../../utils/api';
import { format } from 'date-fns';
import { ru, enUS, ka, et, hy, az } from 'date-fns/locale';
import { FaEye, FaCheck, FaTimes, FaFilter, FaSearch } from 'react-icons/fa';
import '../../styles/AdminOrders.css';
import { useTranslation } from 'react-i18next';

const AdminOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  const getDateLocale = () => {
    const locales = { ru, en: enUS, et, ka, hy, az };
    return locales[i18n.language] || et;
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const response = await ordersAPI.getOrders(params);
      setOrders(response.data.data);
    } catch (error) {
      console.error('Ошибка загрузки заказов:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) {
      alert('Выберите новый статус');
      return;
    }

    try {
      await ordersAPI.updateStatus(selectedOrder._id, {
        status: newStatus,
        comment: statusComment
      });
      
      setMessage({ type: 'success', text: 'Статус заказа обновлен' });
      setShowModal(false);
      setSelectedOrder(null);
      setNewStatus('');
      setStatusComment('');
      loadOrders();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Ошибка обновления статуса' 
      });
    }
  };

  const handleQuickAction = async (orderId, status, comment) => {
    try {
      await ordersAPI.updateStatus(orderId, { status, comment });
      setMessage({ type: 'success', text: `Заказ ${status === 'confirmed' ? 'подтвержден' : 'отклонен'}` });
      loadOrders();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Ошибка обновления заказа' 
      });
    }
  };

  const openStatusModal = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setShowModal(true);
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Ожидает',
      confirmed: 'Подтвержден',
      processing: 'В обработке',
      completed: 'Выполнен',
      cancelled: 'Отменен',
      rejected: 'Отклонен'
    };
    return labels[status] || status;
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
      rejected: 'status-rejected'
    };
    return classes[status] || '';
  };

  // Фильтруем заказы по поисковому запросу
  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const orderNumber = (order.orderNumber || '').toLowerCase();
    const customerName = (order.customer?.restaurantName || order.customer?.email || '').toLowerCase();
    
    return orderNumber.includes(query) || customerName.includes(query);
  });

  return (
    <div className="admin-orders-page">
      <div className="admin-orders-header">
        <h1>Управление заказами</h1>
        
        <div className="header-controls">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Поиск по номеру или клиенту..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="filter-controls">
            <FaFilter />
            <select 
              value={filterStatus} 
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setSearchParams(e.target.value !== 'all' ? { status: e.target.value } : {});
              }}
            >
              <option value="all">Все заказы</option>
              <option value="pending">Ожидают подтверждения</option>
              <option value="confirmed">Подтвержденные</option>
              <option value="processing">В обработке</option>
              <option value="completed">Выполненные</option>
              <option value="cancelled">Отмененные</option>
              <option value="rejected">Отклоненные</option>
            </select>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="loading">Загрузка заказов...</div>
      ) : filteredOrders.length === 0 ? (
        <div className="no-data">
          {searchQuery ? 'Заказы не найдены по запросу' : 'Заказов не найдено'}
        </div>
      ) : (
        <div className="orders-table">
          <table>
            <thead>
              <tr>
                <th>Номер</th>
                <th>Клиент</th>
                <th>{t(`createdDate`, 'Created date')}</th>
                <th>Доставка</th>
                <th>Товаров</th>
                <th>Сумма</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr key={order._id}>
                  <td>
                    <strong>{order.orderNumber}</strong>
                  </td>
                  <td>{order.customer?.restaurantName || order.customer?.email}</td>
                  <td>
                    {format(new Date(order.createdAt), 'd MMM yyyy, HH:mm', { locale: getDateLocale() })}
                  </td>
                  <td>
                    {format(new Date(order.deliveryDate), 'd MMM yyyy', { locale: getDateLocale() })}
                  </td>
                  <td>{order.items.length}</td>
                  <td><strong>{order.total.toFixed(2)} €</strong></td>
                  <td>
                    <span className={`status-badge ${getStatusClass(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => navigate(`/admin/orders/${order._id}`)}
                        className="btn-icon view"
                        title="Подробнее"
                      >
                        <FaEye />
                      </button>

                      {order.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleQuickAction(order._id, 'confirmed', 'Заказ подтвержден администратором')}
                            className="btn-icon confirm"
                            title="Подтвердить"
                          >
                            <FaCheck />
                          </button>
                          <button
                            onClick={() => handleQuickAction(order._id, 'rejected', 'Заказ отклонен администратором')}
                            className="btn-icon reject"
                            title="Отклонить"
                          >
                            <FaTimes />
                          </button>
                        </>
                      )}

                      <button
                        onClick={() => openStatusModal(order)}
                        className="btn-small"
                      >
                        Изменить статус
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Изменить статус заказа #{selectedOrder.orderNumber}</h2>
              <button onClick={() => setShowModal(false)} className="modal-close">×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Текущий статус:</label>
                <div className={`status-badge ${getStatusClass(selectedOrder.status)}`}>
                  {getStatusLabel(selectedOrder.status)}
                </div>
              </div>

              <div className="form-group">
                <label>Новый статус:</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="form-select"
                >
                  <option value="pending">{t(`waitsForConfirmation`, 'Waits for confirmation')}</option>
                  <option value="confirmed">Подтвержден</option>
                  <option value="processing">В обработке</option>
                  <option value="completed">Выполнен</option>
                  <option value="cancelled">Отменен</option>
                  <option value="rejected">Отклонен</option>
                </select>
              </div>

              <div className="form-group">
                <label>Комментарий (опционально):</label>
                <textarea
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  placeholder="Добавьте комментарий к изменению статуса..."
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button onClick={handleStatusChange} className="btn-primary">
                  Обновить статус
                </button>
                <button onClick={() => setShowModal(false)} className="btn-secondary">
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;

